import {
  CostTier,
  MealPlan,
  MealPlanAssignment,
  MealPlanCostEstimate,
  MealPlanDineOutEstimate,
  MealType,
  Recipe,
  getAssignmentMealType,
} from '../types/mealPlan';

/**
 * Loading-state key for a substitution. Dinner keeps the bare day string
 * (pre-slot behavior), breakfast/lunch are suffixed (`Monday#lunch`) so a
 * lunch swap can't put the same day's dinner card (or a leftover-lunch
 * reference card watching that dinner) into a spinner.
 */
export const substitutionKey = (day: string, mealType: MealType) =>
  mealType === 'dinner' ? day : `${day}#${mealType}`;

export const assignmentSubstitutionKey = (assignment: MealPlanAssignment) =>
  substitutionKey(assignment.plannedDay, getAssignmentMealType(assignment));

/**
 * The assignment a reference draws its recipe from. Slot-aware: a reference
 * anchors to the assignment at its preparedOnDate with the SAME meal slot
 * when one holds a recipe (batch rotation blocks), falling back to the
 * dinner there (leftover lunches, dinner prep days). Mirrors the backend's
 * referencesFeeding rule.
 */
export const getReferenceAnchor = (
  assignment: MealPlanAssignment,
  mealPlan?: MealPlan
): MealPlanAssignment | undefined => {
  if (assignment.source !== 'assignment_reference' || !assignment.preparedOnDate || !mealPlan) {
    return undefined;
  }
  const refSlot = getAssignmentMealType(assignment);
  const sameSlot = mealPlan.assignments.find(
    a =>
      a.plannedDate === assignment.preparedOnDate &&
      getAssignmentMealType(a) === refSlot &&
      a.source !== 'assignment_reference' &&
      !!a.scaledRecipe
  );
  if (sameSlot) return sameSlot;
  return mealPlan.assignments.find(
    a =>
      a.plannedDate === assignment.preparedOnDate &&
      getAssignmentMealType(a) === 'dinner' &&
      !!a.scaledRecipe
  );
};

/**
 * Resolves the recipe for an assignment, handling references (prep days,
 * leftover lunches, batch rotation days) via getReferenceAnchor. For other
 * assignments, returns the assignment's scaledRecipe.
 */
export const getAssignmentRecipe = (
  assignment: MealPlanAssignment,
  mealPlan?: MealPlan
): Recipe | undefined => {
  if (assignment.source === 'assignment_reference') {
    return getReferenceAnchor(assignment, mealPlan)?.scaledRecipe;
  }
  return assignment.scaledRecipe;
};

const round2 = (n: number): number => parseFloat(n.toFixed(2));

/**
 * Client-side aggregation mirroring backend `aggregateMealPlanCost`.
 * Strict: null unless every non-reference assignment's scaledRecipe has a
 * costEstimate. Used so plan-level totals stay in sync after carousel swaps
 * without a round trip.
 */
export const aggregateMealPlanCost = (
  mealPlan: MealPlan,
  // When set, only that slot's recipes are aggregated (the dine-out savings
  // comparison is dinner vs dinner; the groceries total stays plan-wide)
  mealType?: MealType
): MealPlanCostEstimate | null => {
  const scaled = mealPlan.assignments
    .filter((a) => mealType === undefined || getAssignmentMealType(a) === mealType)
    .map((a) => a.scaledRecipe)
    .filter((r): r is Recipe => !!r);
  if (scaled.length === 0) return null;
  if (scaled.some((r) => !r.costEstimate)) return null;

  let total: CostTier = { value: 0, mid: 0, premium: 0 };
  let totalServings = 0;
  const byRecipe: MealPlanCostEstimate['byRecipe'] = [];

  for (const recipe of scaled) {
    const est = recipe.costEstimate!;
    const recipeTotal: CostTier = {
      value: round2(est.perServing.value * recipe.servings),
      mid: round2(est.perServing.mid * recipe.servings),
      premium: round2(est.perServing.premium * recipe.servings),
    };
    total = {
      value: total.value + recipeTotal.value,
      mid: total.mid + recipeTotal.mid,
      premium: total.premium + recipeTotal.premium,
    };
    totalServings += recipe.servings;
    byRecipe.push({
      recipeName: recipe.name,
      servings: recipe.servings,
      cost: recipeTotal,
    });
  }

  const perServing: CostTier =
    totalServings > 0
      ? {
          value: round2(total.value / totalServings),
          mid: round2(total.mid / totalServings),
          premium: round2(total.premium / totalServings),
        }
      : { value: 0, mid: 0, premium: 0 };

  const firstEst = scaled[0].costEstimate!;
  return {
    total: { value: round2(total.value), mid: round2(total.mid), premium: round2(total.premium) },
    perDay: { value: 0, mid: 0, premium: 0 },
    perServing,
    byRecipe,
    estimatedAt: firstEst.estimatedAt,
    locationId: firstEst.locationId,
  };
};

// A restaurant entrée holds more food than a single home recipe "serving," and
// `estimatedDineOutPricePerServing` is an entrée (per-order) price. So replacing
// N home servings of food doesn't take N restaurant orders — it takes fewer.
// This matters most because a recipe's serving count is inflated by batch-cooked
// leftovers (one cook day scaled to feed several days); pricing every one of
// those servings as a separate restaurant order overstates the "if you ate out"
// total. We divide home servings by this factor to get restaurant orders.
//
// MUST stay in sync with the backend constant of the same name in
// ingredientDatabaseService.ts (this client copy exists so the draft summary
// recomputes live as recipes are swapped, without a server round trip).
const HOME_SERVINGS_PER_RESTAURANT_ORDER = 1.25;

export const aggregateMealPlanDineOut = (
  mealPlan: MealPlan
): MealPlanDineOutEstimate | null => {
  // Dinner-scoped on purpose: the estimates and the 1.25 portion factor are
  // anchored to restaurant entrees, and "you'd have dined out instead" is
  // only a credible counterfactual for dinner. Pricing a week of home
  // breakfasts as daily diner trips roughly doubled the banner in a way
  // nobody believes. Scoping also means an unpriced breakfast can't null
  // the dinner comparison. Mirrors the backend aggregation's scope.
  const scaled = mealPlan.assignments
    .filter((a) => getAssignmentMealType(a) === 'dinner')
    .map((a) => a.scaledRecipe)
    .filter((r): r is Recipe => !!r);
  if (scaled.length === 0) return null;
  if (scaled.some((r) => !r.estimatedDineOutPricePerServing)) return null;

  let total = 0;
  let totalServings = 0;
  const byRecipe: MealPlanDineOutEstimate['byRecipe'] = [];
  for (const recipe of scaled) {
    const perServing = recipe.estimatedDineOutPricePerServing!.usd;
    // Convert home servings → restaurant orders before pricing (see constant).
    const orders = recipe.servings / HOME_SERVINGS_PER_RESTAURANT_ORDER;
    total += perServing * orders;
    totalServings += recipe.servings;
    byRecipe.push({
      recipeName: recipe.name,
      servings: recipe.servings,
      perServingUsd: perServing,
    });
  }
  const perServing = totalServings > 0 ? total / totalServings : 0;
  return {
    total: round2(total),
    perServing: round2(perServing),
    byRecipe,
  };
};
